import { memo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useI18n } from 'builder/hooks/useI18n'
import { SectionNames, actions, selectors } from 'builder/modules/resumeEditor'
import RichTextArea from 'builder/components/RichTextArea'
import { SectionBodyFoldableContainer, SectionHeader } from 'builder/components/Section'
import { TunerSectionTooltips } from 'builder/components/Tuner'
import SectionTitle from 'builder/components/SectionTitle'
import { useUpdateSimpleField } from 'builder/hooks/useUpdateSimpleField'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { SectionContainer, SectionContent } from './styles'

type Props = {
  isStepByStepFlow?: boolean
  isFoldable?: boolean
}

const PowerStatement = ({ isStepByStepFlow, isFoldable = true }: Props) => {
  const { i18n } = useI18n()
  const { updateSimpleField } = useUpdateSimpleField()
  const dispatch = useDispatch()

  const resume = useTypedSelector(selectors.resume)
  const value = resume?.powerStatement
  const sectionTitles = resume?.sectionTitles
  const locale = resume?.locale
  const customTitle = sectionTitles?.powerStatement
  const isOpened = useTypedSelector(store =>
    selectors.isSectionOpened(store, SectionNames.powerStatement),
  )

  const handleDescriptionChange = useCallback(
    (value: string) => updateSimpleField({ name: 'powerStatement', value, debounce: true }),
    [updateSimpleField],
  )

  const handleRename = useCallback(
    (value: string | null) => {
      dispatch(actions.renameSection({ id: SectionNames.powerStatement, value, debounce: true }))
    },
    [dispatch],
  )

  const handleToggle = useCallback(() => {
    if (!isFoldable) return
    dispatch(actions.setOpenedSections({ [SectionNames.powerStatement]: !isOpened }))
  }, [dispatch, isFoldable, isOpened])

  return (
    <SectionContainer>
      <SectionHeader isOpened={isOpened} onClick={handleToggle} isStepByStepFlow={isStepByStepFlow}>
        <SectionTitle
          title={i18n.t('builder.resume_editor.power_statement_section.title')}
          customTitle={customTitle}
          onRename={handleRename}
          renamable
          isOpened={isOpened}
          isFoldable={isFoldable}
        />
      </SectionHeader>
      <SectionBodyFoldableContainer isOpened={isOpened} isStepByStepFlow={isStepByStepFlow}>
        <SectionContent>
          <RichTextArea value={value || ''} onChange={handleDescriptionChange} locale={locale} />
        </SectionContent>

        <TunerSectionTooltips sectionId={SectionNames.profile} />
      </SectionBodyFoldableContainer>
    </SectionContainer>
  )
}

export default memo(PowerStatement)
